import React, { useEffect, useState } from "react";
import {
  Paper,
  Button,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { useStore } from "../../app/stores/store";
import DataTable, { DataType } from "../../app/common/DataTable";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MemberDocument } from "../../app/common/interfaces/MemberDocument";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import { Formik, Form } from "formik";

export default function MemberDocuments() {
  const { t } = useTranslation();
  const { documentStore, modalStore } = useStore();
  const { id } = useParams();
  const [file, setFile] = useState<File | null>(null);

  const columns = [
    { key: "name", header: t("memberDocuments.name") },
    {
      key: "uploadedAt",
      header: t("memberDocuments.uploadedAt"),
      dataType: DataType.DATE,
    },
    {
      key: "description",
      header: t("memberDocuments.description"),
      dataType: DataType.TEXT,
    },
  ];

  useEffect(() => {
    if (id) {
      documentStore.loadDocumentsForMember(id);
    }
  }, [documentStore.loadDocumentsForMember, id]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async (values: { description: string }) => {
    if (file && id) {
      await documentStore.uploadDocument(id, file, values.description);
      setFile(null);
      documentStore.loadDocumentsForMember(id);
    }
  };

  const handleDownload = async (item: MemberDocument) => {
    await documentStore.downloadDocument(item);
  };

  const handleDelete = async (id: string) => {
    await documentStore.deleteDocument(id);
    modalStore.closeModal();
  };

  const actionButtons = [
    {
      icon: <DownloadIcon />,
      color: "primary",
      onClick: (event: React.MouseEvent, item: MemberDocument) => {
        event.stopPropagation();
        handleDownload(item);
      },
    },
    {
      icon: <DeleteIcon />,
      color: "error",
      onClick: (event: React.MouseEvent, item: MemberDocument) => {
        event.stopPropagation();
        modalStore.openModal(
          <ConfirmComponent
            onYes={async () => handleDelete(item.id!)}
            onCancel={modalStore.closeModal}
            text={t("members.deleteConfirmation")}
          />
        );
      },
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t("memberDocuments.uploadButton")}
        </Typography>
        <Formik initialValues={{ description: "" }} onSubmit={handleUpload}>
          {({ handleSubmit, isValid, isSubmitting, dirty, setFieldValue }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                  >
                    {t("memberDocuments.uploadButton")}
                    <input
                      type="file"
                      hidden
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        handleFileChange(event);
                        setFieldValue("file", event.currentTarget.files?.[0]);
                      }}
                    />
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    placeholder={t("memberDocuments.description")}
                    name="description"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Button
                    variant="contained"
                    color="success"
                    type="submit"
                    disabled={isSubmitting || !dirty || !isValid}
                    fullWidth
                  >
                    {t("general.confirm")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Paper>
      <DataTable
        columns={columns}
        data={documentStore.tableData}
        actionButtons={actionButtons}
        disableRowClick={true}
      />
    </>
  );
}
